import React, { useState, useEffect, useMemo } from 'react';
import logoImage from '../../assets/images/logo/logo.png';

interface SplashScreenProps {
  onFinish: () => void;
  duration?: number;
}

const SplashScreen: React.FC<SplashScreenProps> = ({ onFinish, duration = 3000 }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const particles = useMemo(
    () =>
      [...Array(6)].map((_, i) => ({
        left: `${12 + (i * 17) % 76}%`,
        top: `${10 + (i * 19) % 72}%`,
        animationDelay: `${(i * 0.35) % 2}s`,
        animationDuration: `${2.2 + (i % 3) * 0.4}s`,
      })),
    []
  );

  useEffect(() => {
    const loadingInterval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(loadingInterval);
          return 100;
        }
        return prev + 2;
      });
    }, duration / 50);

    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onFinish, 500);
    }, duration);

    return () => {
      clearTimeout(timer);
      clearInterval(loadingInterval);
    };
  }, [onFinish, duration]);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 bg-white z-[9999] flex flex-col items-center justify-center transition-opacity duration-500 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      dir="rtl"
    >
      {/* Logo Container */}
      <div className="relative mb-8">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-500 rounded-full blur-2xl opacity-30 animate-pulse" />

        <div className="relative bg-gradient-to-br from-white to-orange-50 p-6 rounded-3xl border-4 border-orange-600 shadow-2xl">
          <img
            src={logoImage}
            alt="منصة مستر وليد المصري"
            className="h-20 w-auto object-contain animate-bounce"
          />
        </div>

        <div className="absolute inset-0 rounded-3xl border-4 border-transparent border-t-orange-500 border-r-orange-500 animate-spin" />
      </div>

      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">منصة مستر وليد المصري</h2>
        <p className="text-gray-600">جاري التحميل...</p>
      </div>

      <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-orange-500 via-amber-400 to-orange-600 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${loadingProgress}%` }}
        />
      </div>

      <div className="flex gap-2 mt-6">
        <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>

      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((p, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-orange-400 rounded-full opacity-60 animate-ping"
            style={{
              left: p.left,
              top: p.top,
              animationDelay: p.animationDelay,
              animationDuration: p.animationDuration,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default SplashScreen;
